import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * WorkoutService
 * Centraliza as operações de banco de dados para os Treinos de Natação.
 * Coleção: tenants/{idTenant}/workouts
 */
const WorkoutService = {
    /**
     * Busca os treinos para uma data específica.
     * @param {string} idTenant
     * @param {string} date - Formato "YYYY-MM-DD"
     */
    getWorkoutsByDate: async (idTenant, date) => {
        if (!idTenant || !date) throw new Error("ID do Tenant e data são obrigatórios.");

        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.WORKOUTS),
                where("date", "==", date),
                orderBy("createdAt", "asc")
            );
            const querySnapshot = await getDocs(q);
            const workouts = [];
            querySnapshot.forEach((docSnap) => {
                workouts.push({ id: docSnap.id, ...docSnap.data() });
            });
            return workouts;
        } catch (error) {
            console.error("Erro ao listar treinos por data:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza um treino.
     * @param {Object} workoutData
     * @param {string} idTenant
     * @param {Object} authUser
     */
    save: async (workoutData, idTenant, authUser) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const parsedData = {
            ...workoutData,
            idTenant,
            updatedAt: serverTimestamp()
        };

        try {
            if (workoutData.id) {
                // UPDATE
                const workoutRef = doc(db, "tenants", idTenant, "workouts", workoutData.id);
                const { id, ...dataToUpdate } = parsedData;

                await updateDoc(workoutRef, dataToUpdate);

                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.WORKOUTS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: workoutData.id,
                    entityName: workoutData.title || `Treino de ${workoutData.date}`,
                    description: `Atualizou o treino "${workoutData.title}" do dia ${workoutData.date}`
                });

                return parsedData;
            } else {
                // CREATE
                parsedData.createdAt = serverTimestamp();
                parsedData.createdBy = authUser?.uid || null;

                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.WORKOUTS), parsedData);

                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.WORKOUTS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: workoutData.title || `Treino de ${workoutData.date}`,
                    description: `Criou novo treino "${workoutData.title}" para o dia ${workoutData.date}`
                });

                return { id: newDocRef.id, ...parsedData };
            }
        } catch (error) {
            console.error("Erro no WorkoutService.save:", error);
            throw error;
        }
    },

    /**
     * Remove um treino.
     */
    delete: async (workoutId, idTenant, authUser, workoutTitle, date) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, "workouts", workoutId));

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.WORKOUTS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: workoutId,
                entityName: workoutTitle || `Treino de ${date}`,
                description: `Excluiu o treino "${workoutTitle || workoutId}" do dia ${date}`
            });

            return true;
        } catch (error) {
            console.error("Erro no WorkoutService.delete:", error);
            throw error;
        }
    }
};

export default WorkoutService;
